import { useEffect, useState } from "react";

import type { DateDisplayFormat } from "../utils/date-utils";
import { formatDate, isDateInRange, processDateInput } from "../utils/date-utils";

/**
 * Custom hook to manage date picker state and logic
 */
export const useDatePickerState = (
	value: Date | null | undefined,
	onChange: ((date: Date | null) => void) | ((date: Date) => void),
	allowEmpty?: boolean,
	startDate?: Date,
	endDate?: Date,
	showTimeDropdown?: boolean,
	dateFormat?: DateDisplayFormat,
	defaultTime?: number
) => {
	const [open, setOpen] = useState(false);
	const [month, setMonth] = useState<Date | undefined>(value || undefined);
	const [inputValue, setInputValue] = useState(formatDate(value || undefined, showTimeDropdown, dateFormat));
	const [isTyping, setIsTyping] = useState(false);

	// Update input value when prop value changes (but not while user is typing)
	useEffect(() => {
		if (!isTyping) {
			setInputValue(formatDate(value || undefined, showTimeDropdown, dateFormat));
		}
	}, [value, isTyping, showTimeDropdown, dateFormat]);

	// Update month when value changes
	useEffect(() => {
		if (value) {
			setMonth(value);
		} else {
			// When value is cleared, reset to current month
			setMonth(new Date());
		}
	}, [value]);

	// Reset month to selected date's month when calendar opens
	useEffect(() => {
		if (open) {
			if (value) {
				// Reset to selected date's month
				setMonth(value);
			} else {
				// Reset to current month if no date selected
				setMonth(new Date());
			}
		}
	}, [open, value]);

	// The hour a typed date-only value lands on. Mirrors the calendar, which only applies a time
	// when it shows the time dropdown, and seeds that time from the current value when there is
	// one so editing a date doesn't move its time of day.
	const typedTimeOfDay = showTimeDropdown ? (value ? value.getHours() : defaultTime) : undefined;

	const processInput = (inputValue: string) => {
		const result = processDateInput(inputValue, value, startDate, endDate, allowEmpty, typedTimeOfDay);

		if (result.shouldReset) {
			setInputValue(formatDate(value || undefined, showTimeDropdown, dateFormat));
		} else if (result.date !== value) {
			onChange(result.date as Date);
			if (result.date) {
				setInputValue(formatDate(result.date, showTimeDropdown, dateFormat));
				setMonth(result.date);
			}
		}

		return result;
	};

	const handleInputChange = (newValue: string) => {
		setIsTyping(true);
		setInputValue(newValue);

		// Handle clearing of date when input is emptied
		// When user clears the input field completely, set date to null
		// and reset calendar to current month for better UX
		if (allowEmpty && newValue === "") {
			(onChange as (date: Date | null) => void)(null);
			setMonth(new Date()); // Reset calendar display to current month
			setIsTyping(false); // Exit typing mode since input is cleared
		}
	};

	const handleInputComplete = () => {
		setIsTyping(false);
		processInput(inputValue);
	};

	const handleDateSelect = (selectedDate: Date | undefined) => {
		setIsTyping(false);

		if (selectedDate) {
			// Validate against min/max constraints
			if (!isDateInRange(selectedDate, startDate, endDate)) return;

			// Update both the value and format the input
			onChange(selectedDate);
			setInputValue(formatDate(selectedDate, showTimeDropdown, dateFormat));
		} else if (allowEmpty) {
			(onChange as (date: Date | null) => void)(null);
			setInputValue("");
		}

		// Always close the popover after selecting a date
		setOpen(false);
	};

	const handleClear = () => {
		if (allowEmpty) {
			(onChange as (date: Date | null) => void)(null);
		}
		setInputValue("");
		setMonth(new Date());
	};

	return {
		// State
		open,
		setOpen,
		month,
		setMonth,
		inputValue,
		isTyping,
		// Handlers
		handleInputChange,
		handleInputComplete,
		handleDateSelect,
		handleClear,
	};
};
